// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getNotifyStyle } from "@ces/App/Components/Notify";
import xProductPartNumbersStyles from "@ces/App/SharedStyles/ProductPartNumbersStyles";
import { ProductBrandStyles } from "@ces/Overrides/Components/ProductBrand";
import Minus from "@ces/XDL/Icons/Minus";
import Plus from "@ces/XDL/Icons/Plus";
import Trash from "@ces/XDL/Icons/Trash";
import { fontConfig } from "@ces/XDL/PreStyle/typography";
import { color, createLinkIconStyles, ellipsis, xScreen } from "@ces/XDL/Utilities";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

export interface CartLineSharedStyles {
    productImage?: ProductImageStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    addToListLink?: LinkPresentationProps;
    quantityHeadingAndText?: SmallHeadingAndTextStyles;
    quantityTextField?: TextFieldPresentationProps;
    qtyLabel?: TypographyProps;
    productPrice?: ProductPriceStyles;
    extendedUnitNetPriceText?: SmallHeadingAndTextStyles;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeValueText?: TypographyPresentationProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionsContainer?: InjectableCss;
    promotionNameLabel?: TypographyPresentationProps;
    promotionNameText?: InjectableCss;
    removeCartLineIcon?: IconPresentationProps;
    cartLineNotes?: CartLineNotesStyles;
    cartLineErrorMessageGridItem?: GridItemProps;
    errorMessage?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    configurationOptionText?: TypographyProps;
    configurationOptionLabel?: TypographyProps;
    unitOfMeasure?: TypographyProps;
    lineNotesLinkAdd?: LinkPresentationProps;
    lineNotesLinkHide?: LinkPresentationProps;
    productBrand?: ProductBrandStyles;
    badgesContainer?: InjectableCss;
    actionsWrapper?: InjectableCss;
}

const iconsSharedStyles: any = {
    iconStyles: {
        size: 11,
        css: css`
            &,
            & > svg {
                height: 13px;
            }
        `,
    },
    typographyStyles: {
        lineHeight: "15px",
        weight: fontConfig.weight.bold,
        css: css`
            white-space: nowrap;
        `,
    },
};

const cartLineSharedStyles: CartLineSharedStyles = {
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 91px;
                height: 91px;
                background-color: ${color("common.white")};
                justify-content: center;
                &&& > img {
                    max-width: 100%;
                    max-height: 100%;
                    height: auto;
                    object-fit: contain;
                }
            `,
        },
    },
    productDescription: {
        productDetailLink: {
            color: "text",
            typographyProps: {
                size: 14,
                weight: fontConfig.weight.bold,
                css: css`
                    line-height: 17px;
                `,
            },
            css: css`
                display: inline-block;
                align-self: flex-start;
                margin-bottom: 8px;
                height: auto;
                ${ellipsis(2)}
            `,
        },
        productDetailDescription: {
            size: 14,
            weight: fontConfig.weight.bold,
            css: css`
                letter-spacing: 0;
                line-height: 15px;
                height: auto;
                margin-bottom: 24px;

                ${xScreen("lte", "md")} {
                    margin-bottom: 8px;
                }
            `,
        },
    },
    productBrand: {
        nameLink: {
            css: css`
                display: flex;
            `,
        },
    },
    productPartNumbers: {
        container: {
            css: css`
                ${xScreen("lte", "md")} {
                    > div {
                        gap: 0 22px;
                    }
                }
            `,
        },
        erpNumberGridItem: {
            css: css`
                flex-basis: auto;
                word-break: keep-all;
            `,
        },
    },
    addToListLink: {
        ...createLinkIconStyles({
            icon: Plus,
            ...iconsSharedStyles,
        }),
    },
    lineNotesLinkAdd: {
        ...createLinkIconStyles({
            icon: Plus,
            ...iconsSharedStyles,
        }),
    },
    lineNotesLinkHide: {
        ...createLinkIconStyles({
            icon: Minus,
            iconStyles: {
                size: 11,
                css: css`
                    &,
                    & > svg {
                        height: 2px;
                    }
                `,
            },
            typographyStyles: {
                ...iconsSharedStyles.typographyStyles,
            },
        }),
    },
    productPrice: {
        priceLabelText: {
            size: 12,
            weight: fontConfig.weight.regular,
            lineHeight: "15px",
            css: css`
                text-transform: capitalize;
                padding: 0;
                padding-bottom: 2px;
                display: none;
                ${xScreen("lte", "md")} {
                    display: block;
                }
                @media print {
                    &&& {
                        font-size: 14px;
                        line-height: 16px;
                    }
                }
            `,
        },
        priceWrapper: {
            css: css`
                line-height: 19px;
                padding-top: 6px;
            `,
        },
        price: {
            priceText: {
                size: 16,
                weight: fontConfig.weight.bold,
                lineHeight: "19px",
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    size: 12,
                    weight: fontConfig.weight.regular,
                    color: "common.ui5",
                    lineHeight: "15px",
                },
            },
        },
    },
    extendedUnitNetPriceText: {
        extendedStyles: {
            heading: {
                size: 12,
                lineHeight: "15px",
                weight: fontConfig.weight.regular,
                css: css`
                    text-transform: capitalize;
                    padding: 0;
                    margin-bottom: 7px;
                    display: none;
                    ${xScreen("lte", "md")} {
                        display: block;
                    }
                `,
            },
            text: {
                size: 16,
                weight: fontConfig.weight.bold,
                lineHeight: "19px",
                css: css`
                    justify-content: flex-start;
                    display: flex;
                    padding-top: 6px;
                `,
            },
        },
    },
    costCodeGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
        `,
    },
    costCodeLabelText: {
        ...xProductPartNumbersStyles.label,
    },
    costCodeValueText: {
        ...xProductPartNumbersStyles.value,
    },
    configurationOptionText: {
        ...xProductPartNumbersStyles.value,
    },
    configurationOptionLabel: {
        ...xProductPartNumbersStyles.label,
    },
    promotionsContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
        `,
    },
    promotionNameLabel: {
        ...xProductPartNumbersStyles.label,
    },
    promotionNameText: {
        ...xProductPartNumbersStyles.value,
    },
    qtyLabel: {
        as: "label",
        size: 12,
        weight: fontConfig.weight.regular,
        lineHeight: "15px",
        css: css`
            margin-bottom: 8px;
            display: none;
            ${xScreen("lte", "md")} {
                display: block;
            }
        `,
    },
    removeCartLineIcon: {
        src: Trash,
        size: 16,
        color: "text.link",
        css: css`
            &:hover {
                color: ${color("common.ui8")};
            }
        `,
    },
    cartLineNotes: {
        wrapper: {
            css: css`
                margin-top: 4px;
                ${xScreen("lte", "md")} {
                    width: 100%;
                }
            `,
        },
    },
    cartLineErrorMessageGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-top: 12px;
            > ${TypographyStyle} {
                margin-bottom: 0;
            }
        `,
    },
    errorMessage: {
        ...getNotifyStyle("danger"),
    },
    productAvailability: {
        messageGridItem: {
            width: 12,
            css: css`
                & > div {
                    margin-top: 4px;
                }
            `,
        },
        availabilityByWarehouseLinkGridItem: {
            width: 12,
        },
        availabilityByWarehouseLink: {
            css: css`
                margin-left: unset;
            `,
        },
    },
    unitOfMeasure: {
        size: 12,
        weight: fontConfig.weight.regular,
        color: "common.ui5",
        css: css`
            line-height: 15px;
        `,
    },
    badgesContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-top: 8px;
            & > div:empty {
                display: none;
            }
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 8px;
            margin-top: 12px;
            &:empty {
                display: none;
            }
        `,
    },
};

export default cartLineSharedStyles;
